import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALERT_RULES,
  TEAMS_DEFAULTS,
  TEAMS_WEBHOOKS,
} from '@elementary/common-ui/constants/queryKeys';
import { MicrosoftTeamsForm } from 'shared/types';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import useSnackbar from '../useSnackbar';
import * as api from '../../services/settingsApi';
import useSendTracking from './useSendTracking';

type Props = {
  envId?: string;
  onUpdateSuccess?: () => void;
};

const useMicrosoftTeamsIntegration = ({ envId, onUpdateSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const microsoftTeamsWebhooksQuery = useQuery([TEAMS_WEBHOOKS], () =>
    api.fetchMicrosoftTeamsWebhooks(),
  );

  const microsoftTeamsDefaultsQuery = useQuery(
    [TEAMS_DEFAULTS, envId],
    () => api.fetchMicrosoftTeamsDefaults(envId!),
    { enabled: !!envId },
  );

  const microsoftTeamsWebhooksMutation = useMutation(
    (data: MicrosoftTeamsForm) => {
      const reqData = {
        webhooks: data?.webhooks,
      };

      return microsoftTeamsWebhooksQuery.data?.webhooks
        ? api.updateMicrosoftTeamsWebhooks(reqData)
        : api.setMicrosoftTeamsWebhooks(reqData);
    },
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([TEAMS_WEBHOOKS]);
        queryClient.invalidateQueries([ALERT_RULES]);
        openSnackbar({
          message: 'Successfully connected to your Microsoft Teams',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName: trackingEvents?.SET_TEAMS_WEBHOOKS_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.SET_TEAMS_WEBHOOKS_FAILURE,
        }),
    },
  );

  const microsoftTeamsDefaultsMutation = useMutation(
    (data: MicrosoftTeamsForm) => {
      const reqData = {
        defaultWebhookName: data?.defaultWebhookName,
      };

      return microsoftTeamsDefaultsQuery.data?.defaultDestination
        ? api.updateMicrosoftTeamsDefaults(reqData, envId!)
        : api.setMicrosoftTeamsDefaults(reqData, envId!);
    },
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([TEAMS_DEFAULTS]);
        queryClient.invalidateQueries([ALERT_RULES]);
        openSnackbar({
          message: 'Successfully connected to your Microsoft Teams',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName: trackingEvents?.SET_TEAMS_DEFAULT_DESTINATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.SET_TEAMS_DEFAULT_DESTINATION_FAILURE,
        }),
    },
  );

  return {
    microsoftTeamsWebhooksQuery,
    microsoftTeamsDefaultsQuery,
    microsoftTeamsWebhooksMutation,
    microsoftTeamsDefaultsMutation,
    isTeamsConnected: !!microsoftTeamsDefaultsQuery.data?.defaultDestination,
    isTeamsLoading:
      microsoftTeamsWebhooksQuery.isLoading ||
      microsoftTeamsDefaultsQuery.isLoading,
  };
};

export default useMicrosoftTeamsIntegration;
